//----------------------------------------------------------------------------------------------------------------------
// Keyboard Value Reader
//----------------------------------------------------------------------------------------------------------------------

import type {
    DeviceValueReader,
    InputState,
    KeyboardInputState,
    KeyboardValueReaderDefinition,
} from '../../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Types of keyboard input sources
 */
export type KeyboardSourceType = 'key';

/**
 * Options for configuring a keyboard value reader
 */
export interface KeyboardReaderOptions
{
    /**
     * Whether to use delta state instead of current state
     * When true, the source will only return true on the initial key press
     */
    useDelta ?: boolean;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Reads values from keyboard input states
 */
export class KeyboardValueReader implements DeviceValueReader
{
    /**
     * Always 'key' for keyboard readers.
     */
    public readonly sourceType : KeyboardSourceType = 'key';

    /**
     * The key code to monitor (e.g. "KeyA", "Space", "ArrowUp")
     */
    public readonly sourceKey : string;

    /**
     * When true, reads from delta (only true on the frame the key was first pressed/released)
     */
    private readonly useDelta : boolean;

    /**
     * Creates a new KeyboardValueReader
     *
     * @param keyCode - KeyboardEvent.code value (e.g. "KeyA", "Space", "ArrowUp")
     * @param options
     */
    constructor(keyCode : string, options : KeyboardReaderOptions = {})
    {
        this.sourceKey = keyCode;
        this.useDelta = options.useDelta || false;
    }

    /**
     * Gets the value of the key state. Returns undefined for non-keyboard input states.
     *
     * @param state
     */
    public getValue(state : InputState) : boolean | undefined
    {
        // Check if the state is from the correct device type
        if(state.type !== 'keyboard')
        {
            return undefined;
        }

        const keyboardState = state as KeyboardInputState;

        // Get value from either the current state or delta state based on configuration
        if(this.useDelta)
        {
            return keyboardState.delta[this.sourceKey];
        }
        else
        {
            return keyboardState.keys[this.sourceKey];
        }
    }

    /**
     * Creates a KeyboardValueReader from a string representation
     *
     * @param sourceKey - KeyboardEvent.code value (e.g. "KeyA", "Space")
     * @param options
     */
    public static fromString(sourceKey : string, options : KeyboardReaderOptions = {}) : KeyboardValueReader
    {
        return new KeyboardValueReader(sourceKey, options);
    }

    /**
     * Returns a JSON-serializable representation of this keyboard value reader.
     */
    public toJSON() : KeyboardValueReaderDefinition
    {
        return {

            type: 'keyboard',
            sourceType: this.sourceType,
            sourceKey: this.sourceKey,
            options: {
                useDelta: this.useDelta,
            },
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
